import { useNavigate } from "react-router-dom";
import { AppNav } from "@/components/AppNav";
import { BrBox } from "@/components/BrBox";
import { BrBtn } from "@/components/BrBtn";
import { BrLabel } from "@/components/BrLabel";
import { BrTag } from "@/components/BrTag";
import {
  useGames,
  useMe,
  useStats,
  offsetShadow,
  TYPE,
  formatDate,
  formatTimeControl,
  openingLabel,
  resultLetter,
} from "@chess/shared";
import { TcIcon, type TcIconName } from "@/components/TcIcon";
import { useTheme } from "@/theme/ThemeProvider";
import { useViewport } from "@/util/viewport";

interface Props {
  onOpenSettings: () => void;
  onOpenBuilder: () => void;
  onStartTraining: () => void;
}

export function DashboardPage({ onOpenSettings, onOpenBuilder, onStartTraining }: Props) {
  const p = useTheme();
  const { isMobile } = useViewport();
  const nav = useNavigate();
  const { data: me } = useMe();
  const { data: gamesPage } = useGames(5);
  const { data: stats } = useStats();

  const greeting = (me?.display_name ?? me?.email ?? "PLAYER").split(/[\s@.]/)[0].toUpperCase();
  const games = gamesPage?.items ?? [];
  const blundersFixed = stats?.mistakes_fixed_count ?? 0;
  const totalMistakes = stats?.mistakes_count ?? 0;
  const accuracy = totalMistakes > 0 ? Math.round((blundersFixed / totalMistakes) * 100) : 0;

  const ribbons: Array<{ icon: TcIconName; l: string; d: string }> = [
    { icon: "endless", l: "Endless", d: "No clock · keep going" },
    { icon: "bullet", l: "Bullet", d: "1+0 pressure" },
    { icon: "blitz", l: "Blitz", d: "5+0 sweet spot" },
    { icon: "rapid", l: "Rapid", d: "10+0 thinking" },
    { icon: "classical", l: "Classical", d: "30+30 deep" },
  ];

  return (
    <div style={{ minHeight: "100vh", background: p.bg, color: p.fg, fontFamily: TYPE.body, transition: "background .25s" }}>
      <AppNav onOpenSettings={onOpenSettings} />
      <main
        style={{
          maxWidth: 1280,
          margin: "0 auto",
          padding: isMobile ? "20px 16px 48px" : "32px 32px 64px",
          display: "flex",
          flexDirection: "column",
          gap: isMobile ? 20 : 28,
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: 12 }}>
          <div>
            <BrLabel>{new Date().toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" }).toUpperCase()}</BrLabel>
            <h1
              style={{
                fontFamily: TYPE.display,
                fontSize: isMobile ? 38 : 52,
                fontWeight: 800,
                letterSpacing: isMobile ? -1.2 : -2,
                lineHeight: 0.95,
                margin: "6px 0 0",
                textTransform: "uppercase",
              }}
            >
              GOOD TO SEE YOU,<br />{greeting}.
            </h1>
          </div>
          <div style={{ textAlign: isMobile ? "left" : "right" }}>
            <BrLabel>BLUNDERS FIXED</BrLabel>
            <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginTop: 4 }}>
              <span style={{ fontFamily: TYPE.display, fontSize: isMobile ? 36 : 44, fontWeight: 800, letterSpacing: -1.5, lineHeight: 1 }}>{blundersFixed}</span>
              <BrTag bg={p.success} fg={p.successFg}>OF {totalMistakes}</BrTag>
            </div>
          </div>
        </div>

        <BrBox bg={p.fg} fg={p.bg} padding={isMobile ? 22 : 36} shadowSize={isMobile ? 6 : 10}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: isMobile ? "1fr" : "1.4fr 1fr",
              gap: isMobile ? 22 : 36,
              alignItems: "center",
            }}
          >
            <div>
              <BrLabel color={p.bg} style={{ opacity: 0.7 }}>TRAIN NOW</BrLabel>
              <div
                style={{
                  fontFamily: TYPE.display,
                  fontSize: isMobile ? 32 : 48,
                  fontWeight: 800,
                  letterSpacing: -1.5,
                  lineHeight: 0.95,
                  margin: "10px 0 14px",
                  textTransform: "uppercase",
                }}
              >
                FIX TODAY&rsquo;S<br />
                <span style={{ background: p.accent, color: p.accentFg, padding: "0 8px", border: `3px solid ${p.accent}` }}>BLUNDERS</span>
              </div>
              <div style={{ fontSize: 14, color: p.bgAlt, lineHeight: 1.55, fontWeight: 500, marginBottom: 18 }}>
                We&rsquo;ll pull recent mistakes and turn them into drills. Should take ~8 minutes.
              </div>
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                <BrBtn variant="accent" size="lg" onClick={onStartTraining}>Start session →</BrBtn>
                <BrBtn variant="ghost" size="lg" withShadow={false} style={{ color: p.bg, borderColor: p.bg }} onClick={() => nav("/app/train")}>
                  Pick something else
                </BrBtn>
              </div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 0, border: `2px solid ${p.bg}` }}>
              {[
                [String(stats?.games_count ?? 0), "TOTAL GAMES"],
                [String(blundersFixed), "BLUNDERS FIXED"],
                [`${accuracy}%`, "FIX RATE"],
                [String(me?.connected_accounts.length ?? 0), "ACCOUNTS"],
              ].map(([n, l], i) => (
                <div
                  key={l}
                  style={{
                    padding: isMobile ? 14 : 22,
                    borderRight: i % 2 === 0 ? `2px solid ${p.bg}` : "none",
                    borderBottom: i < 2 ? `2px solid ${p.bg}` : "none",
                  }}
                >
                  <div style={{ fontFamily: TYPE.display, fontSize: isMobile ? 28 : 36, fontWeight: 800, letterSpacing: -1, lineHeight: 1 }}>{n}</div>
                  <div style={{ fontSize: 10, color: p.bgAlt, marginTop: 4, fontFamily: TYPE.mono, letterSpacing: 1, fontWeight: 700 }}>{l}</div>
                </div>
              ))}
            </div>
          </div>
        </BrBox>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
            gap: isMobile ? 20 : 24,
          }}
        >
          <div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: 12 }}>
              <div>
                <BrLabel>CONNECTED</BrLabel>
                <h2 style={{ fontFamily: TYPE.display, fontSize: 24, fontWeight: 800, letterSpacing: -0.5, textTransform: "uppercase", margin: "4px 0 0" }}>Accounts</h2>
              </div>
              <BrBtn size="sm" variant="surface" onClick={onOpenSettings}>Manage</BrBtn>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {(me?.connected_accounts ?? []).length === 0 && (
                <BrBox padding={16} shadowSize={4}>
                  <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 8 }}>No accounts linked yet.</div>
                  <BrBtn size="sm" variant="accent" onClick={() => nav("/connect")}>Connect chess.com →</BrBtn>
                </BrBox>
              )}
              {(me?.connected_accounts ?? []).map((a) => {
                const tag =
                  a.state === "error"
                    ? { bg: p.danger, fg: p.dangerFg, label: "✕ ERROR" }
                    : a.state === "live"
                      ? { bg: p.success, fg: p.successFg, label: "● LIVE" }
                      : { bg: p.bg, fg: p.fg, label: "○ PENDING" };
                const sub =
                  a.state === "error"
                    ? a.sync_error ?? "sync failed"
                    : a.state === "live"
                      ? "synced"
                      : "syncing…";
                return (
                  <BrBox key={a.id} padding={14} shadowSize={3}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontFamily: TYPE.display, fontSize: 18, fontWeight: 800, textTransform: "uppercase" }}>{a.provider}</div>
                        <div style={{ fontSize: 11, fontFamily: TYPE.mono, color: a.state === "error" ? p.danger : p.fgMuted, marginTop: 2, letterSpacing: 0.5 }}>
                          {a.external_username} · {sub}
                        </div>
                      </div>
                      <BrTag bg={tag.bg} fg={tag.fg}>{tag.label}</BrTag>
                    </div>
                  </BrBox>
                );
              })}
              <BrBtn variant="ghost" withShadow={false} onClick={onOpenBuilder} style={{ borderStyle: "dashed", padding: "14px 16px" }}>
                + Create routine
              </BrBtn>
            </div>
          </div>

          <div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: 12 }}>
              <div>
                <BrLabel>RECENT</BrLabel>
                <h2 style={{ fontFamily: TYPE.display, fontSize: 24, fontWeight: 800, letterSpacing: -0.5, textTransform: "uppercase", margin: "4px 0 0" }}>Games</h2>
              </div>
              <BrBtn size="sm" variant="ghost" withShadow={false} onClick={() => nav("/app/profile")}>
                View all →
              </BrBtn>
            </div>
            <BrBox padding={0} shadowSize={4}>
              {games.length === 0 && (
                <div style={{ padding: 18, fontSize: 13, color: p.fgMuted, fontWeight: 600 }}>
                  No games yet. Link an account to import your history.
                </div>
              )}
              {games.map((g, i) => {
                const r = resultLetter(g.result);
                const resultBg = r === "W" ? p.success : r === "L" ? p.danger : p.bg;
                const resultFg = r === "W" ? p.successFg : r === "L" ? p.dangerFg : p.fg;
                return (
                  <button
                    key={g.id}
                    onClick={() => nav(`/app/analysis/${g.id}`)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 12,
                      width: "100%",
                      padding: "12px 14px",
                      background: "transparent",
                      color: p.fg,
                      border: "none",
                      borderBottom: i < games.length - 1 ? `2px solid ${p.border}` : "none",
                      cursor: "pointer",
                      fontFamily: TYPE.body,
                      textAlign: "left",
                    }}
                  >
                    <div
                      style={{
                        width: 30,
                        height: 30,
                        background: resultBg,
                        color: resultFg,
                        border: `2px solid ${p.border}`,
                        display: "grid",
                        placeItems: "center",
                        fontSize: 13,
                        fontWeight: 800,
                        fontFamily: TYPE.mono,
                        flexShrink: 0,
                      }}
                    >
                      {r}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13.5, fontWeight: 700 }}>
                        vs <span style={{ fontFamily: TYPE.mono }}>{g.opponent_username ?? "?"}</span>
                        {g.opponent_rating && (
                          <span style={{ fontSize: 11, color: p.fgMuted, marginLeft: 6, fontFamily: TYPE.mono, fontWeight: 700 }}>({g.opponent_rating})</span>
                        )}
                      </div>
                      <div
                        style={{
                          fontSize: 11,
                          color: p.fgMuted,
                          fontFamily: TYPE.mono,
                          fontWeight: 700,
                          letterSpacing: 0.5,
                          marginTop: 2,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          textTransform: "uppercase",
                        }}
                      >
                        {formatTimeControl(g.time_control)} · {openingLabel(g)}
                      </div>
                    </div>
                    <div style={{ textAlign: "right", flexShrink: 0 }}>
                      <div style={{ fontSize: 12, fontFamily: TYPE.mono, fontWeight: 800 }}>{formatDate(g.played_at)}</div>
                    </div>
                  </button>
                );
              })}
            </BrBox>
          </div>
        </div>

        <div>
          <BrLabel>QUICK START</BrLabel>
          <h2
            style={{
              fontFamily: TYPE.display,
              fontSize: 24,
              fontWeight: 800,
              letterSpacing: -0.5,
              textTransform: "uppercase",
              margin: "4px 0 14px",
            }}
          >
            Jump straight in
          </h2>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: isMobile ? "repeat(2, 1fr)" : "repeat(5, 1fr)",
              gap: isMobile ? 10 : 14,
            }}
          >
            {ribbons.map((q) => (
              <button
                key={q.l}
                onClick={onStartTraining}
                style={{
                  background: p.card,
                  color: p.fg,
                  border: `2px solid ${p.border}`,
                  boxShadow: offsetShadow(p, 3, 3),
                  padding: isMobile ? 14 : 18,
                  cursor: "pointer",
                  fontFamily: TYPE.body,
                  textAlign: "left",
                  display: "flex",
                  flexDirection: "column",
                  gap: 4,
                }}
              >
                <TcIcon name={q.icon} size={32} />
                <div style={{ fontSize: 14, fontWeight: 800, textTransform: "uppercase", letterSpacing: 0.5 }}>{q.l}</div>
                <div style={{ fontSize: 11, color: p.fgMuted, fontFamily: TYPE.mono, fontWeight: 700, letterSpacing: 0.3 }}>{q.d}</div>
              </button>
            ))}
          </div>
        </div>
      </main>
    </div>
  );
}
